import { LOINC_LAB_DISPLAY_NAMES } from './loincLabDisplayNames';
import { expandCatalogQueryPhrases, matchCatalogEntries, type QueryPhraseCatalog } from './queryPhraseCatalog';

/**
 * Alternate phrasings for curated labs, so a lookup finds the analyte whichever name the asker
 * reaches for.
 *
 * Two problems make this necessary. Users and clinicians say "apoB", "apo b" and "apolipoprotein
 * B" for one measurement, and the curated display carries only one of those. Separately, an
 * observation's recorded name depends on where it came from: Liza writes the curated abbreviation,
 * while records ingested from outside are stored exactly as their source sent them, typically with
 * the LOINC long name. A single substring matches one convention or the other, never both.
 *
 * Entries hold the long form alongside the colloquial ones, so an expanded search covers both
 * conventions. Aliases are lowercase and compared case-insensitively; the curated display is
 * always searched too and is not repeated here.
 *
 * Labs only. Vitals and surveys have their own naming spread ("BP", "depression screen") but no
 * observed retrieval failures yet, so they are deliberately out of scope.
 */
export const LOINC_LAB_ALIASES: Record<string, string[]> = {
  '1884-6': ['apolipoprotein b', 'apo b', 'apob'],
  '1963-8': ['carbon dioxide, total', 'total co2', 'co2', 'bicarb'],
  '2085-9': ['hdl', 'hdl cholesterol', 'cholesterol in hdl', 'good cholesterol'],
  '2093-3': ['total cholesterol', 'cholesterol total', 'serum cholesterol'],
  '2571-8': ['triglycerides', 'triglyceride', 'trigs'],
  '2857-1': ['psa', 'prostate specific antigen', 'prostate specific ag'],
  '4548-4': ['a1c', 'hba1c', 'hemoglobin a1c', 'glycated hemoglobin', 'glycohemoglobin'],
  '10835-7': ['lp(a)', 'lpa', 'lipoprotein a', 'lipoprotein(a)', 'lipoprotein (a)', 'lipo a'],
  '13457-7': ['ldl', 'ldl cholesterol', 'cholesterol in ldl', 'bad cholesterol'],
  '14635-7': ['vitamin d', 'vit d', '25-hydroxyvitamin d', 'vitamin d 25-hydroxy'],
  '43583-0': ['lp(a)', 'lpa', 'lipoprotein a', 'lipoprotein(a)', 'lipoprotein (a)', 'lipo a'],
};

/** Every name a curated lab is known by: its display first, then its aliases. */
export const getLabSearchPhrases = (loincCode: string): string[] => {
  const display = LOINC_LAB_DISPLAY_NAMES[loincCode];
  if (!display) return [];
  return [display, ...(LOINC_LAB_ALIASES[loincCode] ?? [])];
};

/**
 * Keyed by LOINC code, so a resolved query names codes the caller can pass to an exact `code`
 * search. Only curated labs are eligible: an alias without a display is not a lab this system can
 * name back to the user.
 */
const LAB_PHRASE_CATALOG: QueryPhraseCatalog = Object.fromEntries(
  Object.keys(LOINC_LAB_DISPLAY_NAMES).map((code) => [code, getLabSearchPhrases(code)]),
);

/** The curated lab codes a free-text query could be naming, by display or alias. */
export const matchCuratedLabCodes = (query: string): string[] => matchCatalogEntries(LAB_PHRASE_CATALOG, query);

/**
 * Every name the analytes behind a query are known by, deduplicated — what a search has to look
 * for to cover both the curated abbreviation and the long form an ingested record carries.
 * Empty when the query resolves to no curated lab.
 */
export const expandLabQueryPhrases = (query: string): string[] => expandCatalogQueryPhrases(LAB_PHRASE_CATALOG, query);
